import React, { forwardRef } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import Utils from '../../../utils/utils';
import PERMISSION from '../../../constants/Permissions';
import UnitDetails from '../UnitAssignment/UnitDetails';
import SlotArrivalStatus from './SlotArrivalStatus';
import ActionsDropdown from './ActionsDropdown';
import ShiftSlotDisplay from './ShiftSlotDisplay';
import BundleBadge from '../BundleBadge';

import editPencil from '../../../assets/images/edit-pencil.svg';
import CompassIcon from '../../../components/icons/CompassIcon';
import guaranteedIcon from '../../../assets/images/guaranteed-icon.svg';
import { ReactComponent as Integrated } from '../../../assets/images/badge-integrated.svg';
import BaseBadge from '../../../components/badges/BaseBadge';
import ShiftTypeBadge from '../ShiftTypeBadge';
import { GLOBAL_CALENDAR_SHIFT_TYPES, INTEGRATION_SOURCES } from '../../../constants';
import OnCallSlotBadge from '../OnCallSlotBadge';

const ShiftSlotDesktop = forwardRef(
	(
		{
			slot,
			shift,
			units,
			user,
			isFixedMarkup,
			updateSlotArrivalStatus,
			onConfirm,
			onPrepareCancel,
			handleEditBonusModalButton,
			handleAddBonusModalButton,
			preventGuaranteeOrBonusUpdates,
			onGuaranteedCheckboxChange,
			onGuaranteeBundleChange,
			setShowUnitAssignmentModal,
			setUnitAssignmentSlot,
			getUnitName,
			onAssign,
			routeSlotAction,
			shouldShowRouteBadges,
			facilityFloatSlotAction,
			onMarkSlotAsCallback
		},
		ref
	) => {
		const shouldShowIntegratedBadge = () => {
			if (!slot.has_integration_badge) return false;
			if (slot.partner_name === 'Presbyterian') return true;

			return (
				slot.integration_source_type_code === INTEGRATION_SOURCES.API &&
				(slot.caregiver_id === null || slot.has_assigned_slot_integrated !== false)
			);
		};

		return (
			<div
				ref={ref}
				className={classNames('shift-card row with-padding with-border slot', {
					[`slot__${slot.is_flex ? 'flex' : 'shiftmed'}`]: shouldShowRouteBadges && !!slot.caregiver_id
				})}
			>
				<div className="col-sm-2 row no-gutters d-flex d-sm-none">
					<div className="shift-label col-3">Date</div>
					<div className="col shift-day">
						<span className="week-day">{slot.shift_day}</span>
						<span className="month-date">{slot.shift_date}</span>
					</div>
				</div>
				<div className="col-sm-2 row no-gutters d-flex align-items-center d-sm-none">
					<div className="shift-label col-3">Shift</div>
					<div className="col d-flex d-sm-block">
						<div className="shift-block">
							<div className="shift-interval">
								<span>{Utils.formatToSimpleTime(slot.raw_start_time)} -</span>{' '}
								<span className="text-nowrap">{Utils.formatToSimpleTime(slot.raw_end_time)}</span>
							</div>
						</div>
					</div>
				</div>

				<div
					className={classNames(
						'col-sm-2 col-xl-2 row no-gutters d-none d-sm-flex table-separator h-sm-100 align-items-sm-center slot__date',
						{
							[`slot__date__${slot.is_flex ? 'flex' : 'shiftmed'}`]: shouldShowRouteBadges && !!slot.caregiver_id
						}
					)}
				>
					<div className="shift-day">
						<div>
							<b>{moment.utc(slot.raw_start_time).format('MMM Do')}</b>
						</div>
						<div>{slot.shift_day}</div>
					</div>
					<div className="shift-time">
						<div className="shift-time__row">
							<div className="shift-time__row__title">from</div>
							<div className="shift-time__row__content">{moment.utc(slot.raw_start_time).format('hh:mm A')}</div>
						</div>
						<div className="shift-time__row">
							<div className="shift-time__row__title">to</div>
							<div className="shift-time__row__content">{moment.utc(slot.raw_end_time).format('hh:mm A')}</div>
						</div>
					</div>
				</div>
				<div className="col-sm-2 col-md-2 d-flex flex-column justify-content-sm-center align-items-center table-separator h-100 padded with-gap-4">
					<div className="d-flex flex-column align-items-sm-center justify-content-sm-center with-gap-4">
						<div className="d-flex justify-content-sm-center ">
							{slot.is_guaranteed && (
								<OverlayTrigger
									placement="right"
									trigger={['hover', 'focus']}
									show
									overlay={
										<Tooltip
											delay={{ show: 150, hide: 400 }}
											className="guaranteed-tooltip"
											style={{ fontSize: '1.4rem' }}
										>
											Guaranteed
										</Tooltip>
									}
								>
									<span className="guaranteed-slot">
										<img className="guaranteed-icon" src={guaranteedIcon} alt="Guaranteed shift" />
									</span>
								</OverlayTrigger>
							)}
							<div className="type ml-1 d-flex align-items-center">{slot.resource_type}</div>
						</div>
						<UnitDetails unitId={slot.unit_id} />
					</div>
					<div className="d-flex flex-wrap justify-content-sm-center align-items-md-center with-gap-4 ">
						{shouldShowRouteBadges && !slot.caregiver_id && (
							<div className="d-flex justify-content-sm-center flex-row flex-wrap with-gap-4">
								<ShiftTypeBadge
									shiftType={slot.is_flex ? GLOBAL_CALENDAR_SHIFT_TYPES.FLEX : GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND}
								/>
								{!slot.is_flex && !!slot.is_hybrid && <ShiftTypeBadge shiftType={GLOBAL_CALENDAR_SHIFT_TYPES.FLEX} />}
							</div>
						)}
						{shouldShowIntegratedBadge() && (
							<div className="integrated-shift-badge">
								<Integrated width={12} height={12} />
								Integrated
							</div>
						)}
						{slot.contract_id && (
							<BundleBadge shiftsCount={slot.contract_non_cancelled_slots_count} bundleId={slot.contract_id} />
						)}
						{!slot.is_flex && slot.is_orientation && (
							<OverlayTrigger
								placement="right"
								trigger={['hover', 'focus']}
								show
								overlay={
									<Tooltip delay={{ show: 150, hide: 400 }} className="orientation-tooltip">
										Orientation
									</Tooltip>
								}
							>
								<BaseBadge badgeCopy="Orientation" className="orientation-badge" icon={<CompassIcon />} />
							</OverlayTrigger>
						)}
						{slot.is_on_call_shift && <OnCallSlotBadge slot={slot} />}
					</div>
				</div>

				<div className="col-sm-4 col-xl-3 row no-gutters table-separator h-sm-100 align-items-sm-center">
					<div className="shift-label col-3 d-flex d-sm-none">Caregiver</div>
					<div className="caregiver col justify-content-center">
						<div className="flex-one d-flex justify-content-sm-center flex-column w-100 ">
							<ShiftSlotDisplay slot={slot} isMobile={false} />
						</div>
					</div>
				</div>

				{!isFixedMarkup && (
					<div className="col-sm-1 col-md-1 row no-gutters justify-content-sm-center align-items-center table-separator h-sm-100">
						<div className="shift-block d-flex align-items-center justify-content-sm-center">
							{slot.is_flex ? (
								'N/A'
							) : (
								<>
									<b
										className={classNames('bonus-value', {
											'show-mobile-add': !slot.caregiver_name && !slot.facility_payable_bonus
										})}
									>
										{slot.facility_payable_bonus ? `$${slot.facility_payable_bonus}` : ''}
									</b>
									{slot.facility_payable_bonus ? (
										<img
											className={classNames('edit-pencil-icon', {
												show: !slot.caregiver_name
											})}
											role="presentation"
											src={editPencil}
											alt="Edit"
											onKeyDown={handleEditBonusModalButton}
											onClick={handleEditBonusModalButton}
										/>
									) : (
										<img
											className={classNames('edit-pencil-icon', {
												show: !slot.caregiver_name,
												'add-icon': !slot.facility_payable_bonus && !slot.caregiver_name
											})}
											role="presentation"
											src={editPencil}
											alt="Add"
											onKeyDown={handleAddBonusModalButton}
											onClick={handleAddBonusModalButton}
										/>
									)}
								</>
							)}
						</div>
					</div>
				)}

				{isFixedMarkup && (
					<div className="col-sm-1 col-md-1 row no-gutters justify-content-sm-center align-items-center table-separator h-sm-100">
						<div className="shift-block d-flex align-items-sm-center">
							{!slot.is_flex && Utils.isAuthorizedExact(PERMISSION.EDIT_WAGE, user.permissions) && (
								<b>
									$
									{slot.isRecurring
										? shift.fixedMarkupWage || shift.originalFixedMarkupWage
										: Number(slot.fixed_markup_wage).toFixed(2)}
									/hr
								</b>
							)}
						</div>
					</div>
				)}

				<div className="col-sm-2 row no-gutters table-separator h-sm-100 align-items-sm-center">
					<div className="col d-flex flex-column align-items-md-center slot-arrival-section">
						{slot.is_flex ? 'N/A' : <SlotArrivalStatus slot={slot} updateSlotArrivalStatus={updateSlotArrivalStatus} />}
					</div>
				</div>

				<div className="col-sm-2 col-md-2 row no-gutters justify-content-sm-center align-items-center">
					{slot && (
						<ActionsDropdown
							isFlex={slot.is_flex}
							canGuarantee={!slot.is_orientation && !preventGuaranteeOrBonusUpdates}
							getUnitName={getUnitName}
							hasAvailableUnits={units.length > 0}
							hasUnitsEnabled={user.hasUnitsEnabled}
							onCancel={onPrepareCancel}
							onConfirm={onConfirm}
							onGuaranteedCheckboxChange={onGuaranteedCheckboxChange}
							onGuaranteeBundleChange={onGuaranteeBundleChange}
							permissions={user.permissions}
							setShowUnitAssignmentModal={setShowUnitAssignmentModal}
							setUnitAssignmentSlot={setUnitAssignmentSlot}
							slot={slot}
							onAssign={onAssign}
							onRouteSlot={routeSlotAction}
							onFacilityFloatSlot={facilityFloatSlotAction}
							onMarkSlotAsCallback={onMarkSlotAsCallback}
						/>
					)}
				</div>
			</div>
		);
	}
);

export default ShiftSlotDesktop;
